import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { restartableTask, timeout } from 'ember-concurrency';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div ...attributes>\n  {{#unless this.isAllVisible}}\n    <div class=\'grid-x grid-padding-x\'>\n      {{#if this.searchHasMany}}\n        <div class=\'cell medium-2\'>\n          <div\n            role=\'button\'\n            class=\'link-button\'\n            data-test-search-has-many-toggle\n            {{on \'click\' this.searchHasManyToggle}}\n          >\n            <FaIcon @icon=\'times\' role=\'presentation\' />\n          </div>\n        </div>\n        <div class=\'cell medium-10\'>\n          <input\n            id=\'primary-search\'\n            autocomplete=\'off\'\n            spellcheck=\'false\'\n            type=\'search\'\n            {{on \'input\' this.onSearchInput}}\n            placeholder=\'{{t \"search...\"}}\'\n            {{on-key firstResponder=true}}\n          />\n        </div>\n      {{else}}\n        <div class=\'cell medium-7\'>\n          <div\n            role=\'button\'\n            class=\'link-button\'\n            data-test-search-has-many-toggle-2\n            {{on \'click\' this.searchHasManyToggle}}\n          >\n            <FaIcon @icon=\'search\' role=\'presentation\' />\n          </div>\n          {{#if this.isPrevVisible}}\n            <div\n              role=\'button\'\n              class=\'link-button\'\n              data-test-next-prev-has-many\n              {{on \'click\' (fn this.nextPrevHasMany false)}}\n            >\n              <FaIcon @icon=\'chevron-left\' role=\'presentation\' />\n            </div>\n          {{/if}}\n          {{#if this.isNextVisible}}\n            <div\n              role=\'button\'\n              class=\'link-button\'\n              data-test-next-prev-has-many-2\n              {{on \'click\' (fn this.nextPrevHasMany true)}}\n            >\n              <FaIcon @icon=\'chevron-right\' role=\'presentation\' />\n            </div>\n          {{/if}}\n        </div>\n        <div class=\'cell medium-5\'>\n          {{#if this.loading}}\n            <BbSpinner />\n          {{else}}\n            <span class=\'beebeeLabel\'>{{this.positionString}}</span>\n          {{/if}}\n        </div>\n      {{/if}}\n    </div>\n  {{/unless}}\n</div>\n{{! Block-form output channel: `null` means \"no page override, render the\n    relationship as loaded\". Non-block consumers use `@onPage` instead. }}\n{{yield this.pageItems}}");

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7;
const DEBOUNCE_MS = 500;
let BbHasManyPaginatorComponent = (_class = class BbHasManyPaginatorComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    // Local mutable state (was Classic plain field mutated by set()/setProperties())
    _initializerDefineProperty(this, "pag", _descriptor2, this);
    _initializerDefineProperty(this, "xpag", _descriptor3, this);
    _initializerDefineProperty(this, "searchHasMany", _descriptor4, this);
    _initializerDefineProperty(this, "q", _descriptor5, this);
    _initializerDefineProperty(this, "loading", _descriptor6, this);
    // The current page, as a LOCAL buffer. `null` means "no page override —
    // render the relationship exactly as it was loaded".
    //
    // This component used to paginate by mutating `@relatedHasMany` itself
    // (splice + push), which truncated the record's own hasMany; any later
    // save then PATCHed the truncated list back to the server and dropped the
    // unlisted members. The paginator is now strictly read-only: it never
    // touches `@relatedHasMany`, and publishes each page through `@onPage`
    // (and through `{{yield}}` for block-form consumers).
    // See docs/code-audit/PAGINATOR-RELATIONSHIP-DATA-LOSS.md.
    _initializerDefineProperty(this, "pageItems", _descriptor7, this);
  }
  get filteredTotal() {
    return this.args.total ? this.args.total : 0;
  }
  get positionString() {
    const start = this.xpag * this.pag - this.xpag + 1;
    const end = Math.min(this.xpag * this.pag, this.filteredTotal);
    const total = this.filteredTotal;
    return `da ${start} a ${end} di ${total}`;
  }
  get isAllVisible() {
    return this.filteredTotal <= this.xpag ? true : false;
  }
  get isNextVisible() {
    return this.filteredTotal > this.pag * this.xpag;
  }
  get isPrevVisible() {
    return this.pag > 1;
  }

  // Search-input handler. Replaces `oninput={{perform this.debounceQTask
  // value='target.value'}}`: the classic action-value syntax routes through
  // Ember's ActionState/flaggedInstrument, whose cached callback resolves
  // `undefined` mid-teardown while a lingering native `oninput` still fires
  // → apply-on-undefined (Sentry EMBER-MEDICAL-2JV). `{{on 'input'}}` on a
  // stable @action is auto-detached on teardown, closing that path.
  onSearchInput(event) {
    this.debounceQTask.perform(event.target.value);
  }
  *debounceQTask(value = null) {
    yield timeout(DEBOUNCE_MS);
    this.q = value;
    this.pag = 1;
    this.queryHasManyTask.perform();
  }
  _publishPage(items) {
    this.pageItems = items;
    this.args.onPage?.(items, {
      pag: this.pag,
      q: this.q,
      status: this.args.status?.id
    });
  }
  *queryHasManyTask() {
    yield timeout(DEBOUNCE_MS);

    // Page-1 identity rule: page 1 with no search term is, by definition, the
    // relationship as the server already loaded it. Publish `null` instead of
    // re-querying, so both the first render and every return to page 1 are
    // strict no-ops on the consumer's rendering.
    if (this.pag === 1 && !this.q) {
      this._publishPage(null);
      return;
    }
    var query = {
      pag: this.pag,
      xpag: this.xpag,
      q: this.q,
      status: this.args.status?.id
    };
    query[this.args.item.constructor.modelName + '_id'] = this.args.item.get('id');
    this.loading = true;
    try {
      const items = yield this.store.query(this.args.relatedModelType, query);
      this._publishPage(items.slice());
    } finally {
      this.loading = false;
    }
  }
  nextPrevHasMany(next) {
    if (next) {
      this.pag = this.pag + 1;
    } else {
      this.pag = this.pag - 1;
      if (this.pag < 1) {
        this.pag = 1;
      }
    }
    this.queryHasManyTask.perform();
  }
  searchHasManyToggle() {
    this.searchHasMany = !this.searchHasMany;
    if (!this.searchHasMany) {
      this.q = null;
      this.pag = 1;
      this.queryHasManyTask.perform();
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "pag", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 1;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "xpag", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 10;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "searchHasMany", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "q", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "loading", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "pageItems", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "onSearchInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onSearchInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "debounceQTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "debounceQTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "queryHasManyTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "queryHasManyTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "nextPrevHasMany", [action], Object.getOwnPropertyDescriptor(_class.prototype, "nextPrevHasMany"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "searchHasManyToggle", [action], Object.getOwnPropertyDescriptor(_class.prototype, "searchHasManyToggle"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbHasManyPaginatorComponent);

export { BbHasManyPaginatorComponent as default };
//# sourceMappingURL=bb-has-many-paginator.js.map
